import type { MetricsBlock, ProtocolFilter } from "../types";
import { InfoTip } from "./InfoTip";

interface SummaryCardsProps {
  metrics: MetricsBlock;
  protocolFilter: ProtocolFilter;
}

export function SummaryCards({ metrics }: SummaryCardsProps) {
  const hf = metrics.health_factor_distribution.summary;
  const util = metrics.utilization?.aggregated;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      <MetricCard
        label={<InfoTip text="Total value of assets deposited as collateral or earning interest across all markets.">Total Supplied</InfoTip>}
        value={formatUsd(util?.total_supplied_usd ?? 0)}
      />
      <MetricCard
        label={<InfoTip text="Total value of assets currently borrowed across all markets.">Total Borrowed</InfoTip>}
        value={formatUsd(util?.total_borrowed_usd ?? 0)}
      />
      <MetricCard
        label={<InfoTip text="Collateral-weighted mean health factor across all positions, capped at 20 per position to prevent ultra-safe outliers from skewing the average.">Avg Health Factor</InfoTip>}
        value={hf.weighted_avg_hf.toFixed(2)}
        color={hfColor(hf.weighted_avg_hf)}
      />
      <MetricCard
        label={<InfoTip text="Positions with health factor below 1.1 — close to the liquidation threshold of 1.0. The dollar value is their total collateral.">Positions Near Liq.</InfoTip>}
        value={hf.positions_below_1_1.toLocaleString()}
        subtitle={formatUsd(hf.value_below_1_1_usd)}
        color="text-risk-warning"
      />
      <MetricCard
        label={<InfoTip text="Total wallets with active borrow positions (collateral + debt > $1).">Active Positions</InfoTip>}
        value={hf.total_positions.toLocaleString()}
      />
    </div>
  );
}

function MetricCard({
  label,
  value,
  subtitle,
  color,
}: {
  label: React.ReactNode;
  value: string;
  subtitle?: string;
  color?: string;
}) {
  return (
    <div className="card-compact">
      <p className="metric-label mb-1">{label}</p>
      <p className={`metric-value-sm ${color ?? "text-text-primary"}`}>
        {value}
      </p>
      {subtitle && (
        <p className="text-xs text-text-muted mt-0.5 font-mono">{subtitle}</p>
      )}
    </div>
  );
}

function formatUsd(value: number): string {
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

function hfColor(hf: number): string {
  if (hf < 1.1) return "text-risk-danger";
  if (hf < 1.3) return "text-risk-warning";
  if (hf < 2.0) return "text-risk-moderate";
  return "text-risk-safe";
}
